import { getCurrentScope, onScopeDispose, shallowRef, type ShallowRef } from 'vue';

/** How long a revealed value stays on screen before it masks itself again. */
export const REVEAL_TIMEOUT_MS = 60_000;

/** One shown value's countdown. */
interface Countdown {
  /** When the value was shown, on the wall clock (`Date.now()`) … */
  wallAt: number;
  /** … and on the monotonic clock (`performance.now()`) — see `shownFor`. */
  monoAt: number;
  /** Monotonic time the pending timer fires at. A resync may only move it EARLIER. */
  dueMono: number;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Keyed show/hide state for masked values that re-mask themselves.
 *
 * `toggle(key)` shows a hidden value or hides a shown one. Every reveal starts
 * its own countdown: once `timeoutMs` (60s by default) has passed the value is
 * masked again, so a figure never stays exposed because someone put the phone
 * down on that screen. Keys are independent — showing one never uncovers, or
 * extends the countdown of, another. Showing a value again restarts its window.
 *
 * Unlike `useConfidentialReveal`, showing costs nothing: this is shoulder-surfing
 * protection only.
 *
 * The countdown survives the device sleeping. Browsers throttle timers in a
 * background tab and a mobile WebView can freeze JS entirely while the phone is
 * locked, so a bare `setTimeout` can fire minutes late — leaving the value on
 * screen when the user comes back. The timer is the normal path; the moment the
 * page becomes visible again, each countdown is re-derived from how long its
 * value has REALLY been shown (see `shownFor`): overdue values are masked and
 * the rest are re-armed for exactly what they have left.
 *
 * Call from `setup()` (or any effect scope): the timers and the visibility
 * listener are released when that scope is disposed.
 *
 * ```ts
 * const reveal = useTimedReveal<'salary' | 'dailyRate'>();
 * // template: reveal.isRevealed('salary') ? amount : MASKED
 * ```
 *
 * Pass a non-finite `timeoutMs` (`Infinity`) for plain keyed show/hide with NO
 * countdown — a revealed value then stays up until it is hidden. A
 * {@link useConfidentialReveal} gate can opt into it (`autoHideMs: Infinity`);
 * its default is the 60s window, because a PIN / password proves who looked but
 * says nothing about an unattended screen afterwards.
 */
export function useTimedReveal<K extends string>(timeoutMs: number = REVEAL_TIMEOUT_MS) {
  // shallowRef + reassignment, for the same reason as useConfidentialReveal: a deep
  // ref degrades the generic `K` to `any`, so every mutation below REASSIGNS.
  const revealed: ShallowRef<ReadonlySet<K>> = shallowRef<ReadonlySet<K>>(new Set<K>());
  const countdowns = new Map<K, Countdown>();
  // `Infinity` (or a non-positive value) means "no countdown": show/hide only.
  // Guarded here rather than at the setTimeout, because `setTimeout(fn, Infinity)`
  // coerces the delay to NaN and fires on the NEXT TICK — the opposite of never.
  const autoHides = Number.isFinite(timeoutMs) && timeoutMs > 0;

  function isRevealed(key: K): boolean {
    return revealed.value.has(key);
  }

  /**
   * How long a value has really been on screen: the LARGER of the two clocks'
   * readings. Each can only under-count, and in a different situation — the
   * monotonic clock can stop while the device sleeps; the wall clock loses time
   * when it is set backwards. Taking the larger keeps either one from stretching
   * the window. (A wall clock set FORWARD over-counts, which only masks early —
   * the safe direction.)
   */
  function shownFor(countdown: Countdown): number {
    return Math.max(Date.now() - countdown.wallAt, performance.now() - countdown.monoAt);
  }

  function clearCountdown(key: K): void {
    const countdown = countdowns.get(key);
    if (countdown) clearTimeout(countdown.timer);
    countdowns.delete(key);
  }

  function hide(key: K): void {
    clearCountdown(key);
    if (!revealed.value.has(key)) return;
    const next = new Set(revealed.value);
    next.delete(key);
    revealed.value = next;
  }

  function show(key: K): void {
    // Showing again restarts the window rather than stacking a second timer.
    clearCountdown(key);
    if (!autoHides) {
      if (!revealed.value.has(key)) revealed.value = new Set(revealed.value).add(key);
      return;
    }
    const monoAt = performance.now();
    countdowns.set(key, {
      wallAt: Date.now(),
      monoAt,
      dueMono: monoAt + timeoutMs,
      timer: setTimeout(() => hide(key), timeoutMs),
    });
    if (!revealed.value.has(key)) revealed.value = new Set(revealed.value).add(key);
  }

  /** Eye-button handler: hide a shown value, or show a hidden one. */
  function toggle(key: K): void {
    if (revealed.value.has(key)) hide(key);
    else show(key);
  }

  /**
   * Re-derive every countdown from how long its value has really been shown.
   * Overdue keys are masked; the rest are RE-ARMED for exactly the time they
   * have left. Merely skipping them would be wrong: a timer that slept through a
   * locked phone still owes its full pre-sleep remainder, so unlocking at 50s
   * would leave the value up until ~100s.
   *
   * The new remainder is also capped by when the pending timer was already due,
   * so a resync can only bring a mask FORWARD — no wall-clock change, however
   * many times the user switches apps, can push one later.
   */
  function resyncCountdowns(): void {
    // Snapshot first — hide() deletes from the map being walked.
    for (const [key, countdown] of [...countdowns]) {
      const nowMono = performance.now();
      const remaining = Math.min(timeoutMs - shownFor(countdown), countdown.dueMono - nowMono);
      if (remaining <= 0) {
        hide(key);
        continue;
      }
      clearTimeout(countdown.timer);
      countdown.dueMono = nowMono + remaining;
      countdown.timer = setTimeout(() => hide(key), remaining);
    }
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === 'visible') resyncCountdowns();
  }

  // Only wire the listener where it can be released again; outside an effect
  // scope the timers alone still re-mask on time while the page stays visible.
  if (getCurrentScope() && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    onScopeDispose(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      for (const countdown of countdowns.values()) clearTimeout(countdown.timer);
      countdowns.clear();
    });
  }

  return { isRevealed, toggle, show, hide };
}
